import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, X, Clock, CornerDownLeft } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { mediaUrl, IMG } from '../lib/mediaUrl';
import { track } from '../lib/track';

/**
 * Full-screen search.
 *
 * The old version expanded an input inside the 76px header row, which meant
 * the logo and nav reflowed around it every time it opened, and its 0.9rem
 * font put it under iOS Safari's 16px threshold, so focusing it zoomed the
 * whole page. This takes over the screen instead: the header never moves, the
 * input is 17px so nothing zooms, and you see matching products as you type
 * rather than typing blind and landing on a filtered grid.
 */

const RECENTS_KEY = 'sv_recent_searches';
const MAX_RECENTS = 5;

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? JSON.parse(raw).slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function pushRecent(term) {
  try {
    const next = [term, ...readRecents().filter((t) => t.toLowerCase() !== term.toLowerCase())]
      .slice(0, MAX_RECENTS);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* private mode, or storage disabled. Recents are a convenience, not a feature. */
  }
}

const money = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function SearchOverlay({ open, onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const [cursor, setCursor] = useState(-1);

  useEffect(() => {
    if (!open) return;
    setRecents(readRecents());
    setCursor(-1);
    // A frame's delay so the element exists before focus, which also stops
    // iOS from opening the keyboard against a half-rendered overlay.
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Lock the page behind the overlay. iOS ignores overflow:hidden on body,
  // so the scroll position is captured and restored by hand.
  useEffect(() => {
    if (!open) return;
    const y = window.scrollY;
    const { style } = document.body;
    const prev = { position: style.position, top: style.top, width: style.width };
    style.position = 'fixed';
    style.top = `-${y}px`;
    style.width = '100%';
    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.width = prev.width;
      window.scrollTo(0, y);
    };
  }, [open]);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return; }
    setLoading(true);
    let cancelled = false;
    const t = setTimeout(async () => {
      const like = `%${q}%`;
      const { data } = await supabase
        .from('products')
        .select('id, name, slug, price_cents, quantity_available, set_name, product_media(storage_path, sort_order)')
        .eq('status', 'active')
        .or(`name.ilike.${like},set_name.ilike.${like}`)
        .order('created_at', { ascending: false })
        .limit(8);
      if (cancelled) return;
      setResults(data || []);
      setLoading(false);
      setCursor(-1);
      // Recorded with the hit count, so a term that returns nothing is
      // visible as demand you are not carrying.
      track('search', { label: q, meta: { results: (data || []).length } });
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [term]);

  const photoOf = (p) => {
    const photos = (p.product_media || []).slice().sort((a, b) => a.sort_order - b.sort_order);
    return photos[0] ? mediaUrl(photos[0].storage_path, IMG.thumb) : null;
  };

  const close = () => { setTerm(''); setResults([]); onClose(); };

  const goToTerm = (q) => {
    const clean = (q || '').trim();
    if (clean) pushRecent(clean);
    close();
    navigate(clean ? `/shop?q=${encodeURIComponent(clean)}` : '/shop');
  };

  const goToProduct = (p) => {
    if (term.trim()) pushRecent(term.trim());
    close();
    navigate(`/product/${p.slug}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { close(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % results.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (c <= 0 ? results.length - 1 : c - 1)); }
    if (e.key === 'Enter' && cursor >= 0) { e.preventDefault(); goToProduct(results[cursor]); }
  };

  const showRecents = term.trim().length < 2 && recents.length > 0;
  const showEmpty = term.trim().length >= 2 && !loading && results.length === 0;

  const body = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search products"
      style={{
        position: 'fixed', inset: 0, zIndex: 120,
        background: 'var(--bg, #f4f2ef)',
        display: 'flex', flexDirection: 'column',
        animation: 'svSearchIn 180ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <style>{`
        @keyframes svSearchIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { @keyframes svSearchIn { from { opacity: 1; } to { opacity: 1; } } }
        .sv-sresult:hover, .sv-sresult[data-active="true"] { background: var(--bg-elevated, #fff); }
      `}</style>

      <form
        onSubmit={(e) => { e.preventDefault(); goToTerm(term); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '14px 16px', borderBottom: '1px solid var(--border)',
          background: 'var(--surface, #fff)',
        }}
      >
        <Search size={20} style={{ flexShrink: 0, opacity: 0.55 }} />
        <input
          ref={inputRef}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search cards, sealed, ETBs…"
          aria-label="Search products"
          enterKeyHint="search"
          autoComplete="off"
          style={{
            flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent',
            // 17px on purpose: iOS zooms the page for anything under 16.
            fontSize: 17, fontFamily: 'inherit', color: 'var(--text)',
          }}
        />
        {term && (
          <button
            type="button"
            onClick={() => { setTerm(''); inputRef.current?.focus(); }}
            aria-label="Clear search"
            style={{ display: 'flex', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.5, padding: 4 }}
          >
            <X size={18} />
          </button>
        )}
        <button
          type="button"
          onClick={close}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: 'var(--accent)',
            padding: '6px 2px', flexShrink: 0,
          }}
        >
          Cancel
        </button>
      </form>

      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {showRecents && (
          <div style={{ padding: '10px 6px' }}>
            <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.5, margin: '6px 12px 4px' }}>
              Recent
            </p>
            {recents.map((r) => (
              <button
                key={r}
                type="button"
                className="sv-sresult"
                onClick={() => goToTerm(r)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: '12px 14px', borderRadius: 12, textAlign: 'left',
                  fontFamily: 'inherit', fontSize: 15, color: 'var(--text)',
                }}
              >
                <Clock size={16} style={{ opacity: 0.45, flexShrink: 0 }} />
                {r}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <p style={{ padding: '22px 20px', opacity: 0.55, fontSize: 14 }}>Searching…</p>
        )}

        {results.length > 0 && (
          <div style={{ padding: '10px 6px' }}>
            {results.map((p, i) => {
              const img = photoOf(p);
              const out = (p.quantity_available ?? 0) <= 0;
              return (
                <button
                  key={p.id}
                  type="button"
                  className="sv-sresult"
                  data-active={cursor === i}
                  onClick={() => goToProduct(p)}
                  onMouseEnter={() => setCursor(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    padding: '10px 12px', borderRadius: 14, textAlign: 'left',
                    fontFamily: 'inherit', color: 'var(--text)',
                  }}
                >
                  <div style={{
                    width: 52, height: 52, borderRadius: 10, flexShrink: 0, overflow: 'hidden',
                    background: 'var(--bg-elevated, #eee)',
                  }}>
                    {img && <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{p.name}</div>
                    <div style={{ fontSize: 13, opacity: 0.6, marginTop: 2 }}>
                      {p.set_name ? `${p.set_name} · ` : ''}{out ? 'Sold out' : money(p.price_cents)}
                    </div>
                  </div>
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => goToTerm(term)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: '14px 14px 18px', textAlign: 'left', fontFamily: 'inherit',
                fontSize: 14, fontWeight: 700, color: 'var(--accent)',
              }}
            >
              <CornerDownLeft size={16} />
              See all results for “{term.trim()}”
            </button>
          </div>
        )}

        {showEmpty && (
          <div style={{ padding: '32px 22px' }}>
            <p style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Nothing matched “{term.trim()}”</p>
            <p style={{ fontSize: 14, opacity: 0.6, margin: '6px 0 16px', lineHeight: 1.55 }}>
              Try a set name, a card name, or something broader like “ETB”.
            </p>
            <button
              type="button"
              onClick={() => goToTerm('')}
              style={{
                background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 999,
                padding: '11px 22px', fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Browse everything
            </button>
          </div>
        )}
      </div>
    </div>
  );

  if (!open) return null;
  return createPortal(body, document.body);
}
